import {
  AccountEntry,
  GetPerformanceDataResponse,
  PerformanceData,
} from '@src/features/performance/types/get-performance-data-response';
import { api } from '@src/lib/api-client';
import { QueryConfig } from '@src/lib/react-query';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { formatInterestRate, formatUsd, ToastUtility } from '@utils';
import { parseISO } from 'date-fns';

export async function getPerformanceData(): Promise<GetPerformanceDataResponse> {
  return await api
    .get<GetPerformanceDataResponse>(`/api/v3/user/performance`, {
      headers: {
        accept: 'application/json',
      },
    })
    .then((res) => res.data);
}

export function mapGetPerformanceDataResponseToPerformanceData(
  responseData: GetPerformanceDataResponse,
): PerformanceData {
  return {
    annualizedInterest: formatUsd(responseData.annualizedInterestCents),
    currentInterest: formatInterestRate(responseData.currentInterest),
    interestDetail: responseData.interestDetail?.map(
      (obj): AccountEntry => ({
        accountUuid: obj.accountUuid,
        date: parseISO(obj.adjustedDate),
        amountCents: obj.amountCents,
      }),
    ),
    balanceDetail: responseData.balanceDetail?.map(
      (obj): AccountEntry => ({
        accountUuid: obj.accountUuid,
        date: parseISO(obj.date),
        amountCents: obj.amountCents,
      }),
    ),
    performanceDetail: responseData.performanceDetail?.map((item) => ({
      ...item,
      date: parseISO(item.date),
    })),
    interestYtd: formatUsd(responseData?.interestYtdCents),
    percentFdicInterest: responseData.percentFdicCoverage + '%',
    totalBalance: formatUsd(responseData.totalBalanceCents),
    totalExternalBalance: formatUsd(responseData.totalExternalBalanceCents),
    weightedBasisPoints: formatInterestRate(responseData.weightedBasisPoints),
  };
}

export const getPerformanceDataQueryOptions = queryOptions({
  queryKey: ['performanceData'],
  queryFn: async (): Promise<PerformanceData> => {
    const responseData = await getPerformanceData();
    return mapGetPerformanceDataResponseToPerformanceData(responseData);
  },
  throwOnError: false,
});

type UseGetPerformanceDataOptions = {
  queryConfig?: QueryConfig<typeof getPerformanceDataQueryOptions>;
};

export const useGetPerformanceData = ({
  queryConfig,
}: UseGetPerformanceDataOptions = {}) => {
  return useQuery({
    ...getPerformanceDataQueryOptions,
    ...queryConfig,
  });
};

export const callGetPerformanceDataErrorToast = () => {
  ToastUtility.error('Failed to fetch Performance Data', {
    toastId: 'performanceData',
  });
};
